import { useCallback, useState } from "react";
import { useInRouterContext, useSearchParams } from "react-router-dom";

import { readRemembered, writeRemembered } from "../../../shared/lib/remembered";

type Params = [URLSearchParams, (fn: (current: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void];

/**
 * Параметры адреса, а вне роутера (тесты, отдельные стенды) — просто
 * состояние. Роутер у компонента за жизнь не меняется, так что ветвление
 * хуков здесь безопасно.
 */
export function useSafeSearchParams(): Params {
  if (useInRouterContext()) return useRouterParams();
  return useLocalParams();
}

/**
 * Свой setter поверх роутерного: тот считает от параметров последнего рендера,
 * и два вызова подряд («направление» и «человек» одним нажатием, доска и окно
 * публикации в одном такте) затирали бы друг друга. Здесь вызовы одного такта
 * складываются: следующий считает от результата предыдущего.
 */
let pending: string | null = null;

function useRouterParams(): Params {
  const [params, setSearchParams] = useSearchParams();
  const set = useCallback<Params[1]>(
    (fn, opts) => {
      const next = fn(new URLSearchParams(pending ?? params.toString()));
      if (pending === null) queueMicrotask(() => { pending = null; });
      pending = next.toString();
      setSearchParams(next, opts);
    },
    [params, setSearchParams],
  );
  return [params, set];
}

function useLocalParams(): Params {
  const [params, set] = useState(() => new URLSearchParams());
  const setParams = useCallback((fn: (current: URLSearchParams) => URLSearchParams) => set((p) => fn(new URLSearchParams(p))), []);
  return [params, setParams];
}

/**
 * Фильтр экрана в адресе: «Назад» возвращает его, ссылкой можно поделиться.
 * `remember` — ещё и «последний выбор» в этом браузере: без параметра в
 * адресе экран открывается так, как его оставили.
 *
 * Значение по умолчанию в адрес не пишется — ссылка остаётся короткой.
 */
export function useUrlState(key: string, fallback: string, opts: { remember?: string } = {}): [string, (value: string | null) => void] {
  const [params, setParams] = useSafeSearchParams();
  const fromUrl = params.get(key);
  const value = fromUrl ?? (opts.remember ? readRemembered<string | null>(opts.remember, null) : null) ?? fallback;
  const set = useCallback(
    (next: string | null) => {
      if (opts.remember) writeRemembered(opts.remember, next === fallback ? null : next);
      setParams((current) => {
        const p = new URLSearchParams(current);
        if (next === null || next === fallback) p.delete(key);
        else p.set(key, next);
        return p;
      }, { replace: true });
    },
    [key, fallback, opts.remember, setParams],
  );
  return [value, set];
}

/** То же для «может не быть»: человек, направление — пусто значит «все». */
export function useUrlOptional(key: string, opts: { remember?: string } = {}): [string | null, (value: string | null) => void] {
  const [value, set] = useUrlState(key, "", opts);
  return [value || null, set];
}
